/**
 * Global browser-level error handlers.
 *
 * Catches errors and promise rejections that never reach a React error
 * boundary (async code, event handlers, bare `throw` outside render). These
 * are logged, not surfaced as UI, so a stray rejection can't blank the app.
 *
 * Registration is idempotent across module re-evaluation. React StrictMode
 * double-invokes effects, and — more importantly — Vite HMR re-executes this
 * module body on every hot reload. A plain module-level boolean resets to
 * `false` on each re-eval while the previously-attached listeners stay live,
 * so each reload would leak another listener. To make registration a true
 * no-op regardless of how many times the module is re-evaluated, we:
 *   1. keep the registration state + the *named* handler references on
 *      `globalThis` (survives module re-eval), and
 *   2. detach the live listeners via `import.meta.hot.dispose(...)` before HMR
 *      swaps in the new module instance.
 * Net effect: exactly one live listener of each type at all times.
 */

import { reportError } from './errorLogger';

/** Stable, named handlers so add/removeEventListener reference the same fn. */
function handleUnhandledRejection(event: PromiseRejectionEvent): void {
  reportError(event.reason, 'window.unhandledrejection');
}

function handleError(event: ErrorEvent): void {
  // `event.error` carries the thrown value for script errors; fall back to
  // the event's message for cross-origin/resource errors that omit it.
  reportError(event.error ?? event.message, 'window.onerror');
}

/** Shape of the registry we stash on `globalThis` to survive module re-eval. */
type GlobalErrorHandlerRegistry = {
  onError: typeof handleError;
  onUnhandledRejection: typeof handleUnhandledRejection;
};

/**
 * Symbol-keyed slot on `globalThis`. Using a Symbol (rather than a string
 * property) avoids colliding with anything else on the global object, and the
 * value persists across HMR module re-evaluation and StrictMode re-invocation.
 */
const REGISTRY_KEY = Symbol.for('@franchise-consultant/global-error-handlers');

type GlobalWithRegistry = typeof globalThis & {
  [REGISTRY_KEY]?: GlobalErrorHandlerRegistry;
};

export function registerGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  const globalScope = globalThis as GlobalWithRegistry;

  // Already registered in this realm (e.g. StrictMode re-invoke, or a module
  // re-eval where dispose didn't run) — do nothing.
  if (globalScope[REGISTRY_KEY]) return;

  const registry: GlobalErrorHandlerRegistry = {
    onError: handleError,
    onUnhandledRejection: handleUnhandledRejection,
  };

  window.addEventListener('unhandledrejection', registry.onUnhandledRejection);
  window.addEventListener('error', registry.onError);

  globalScope[REGISTRY_KEY] = registry;
}

/**
 * Detach the currently-registered handlers and clear the registry. Exported so
 * it can be exercised in tests; also invoked from the HMR dispose hook below.
 */
export function unregisterGlobalErrorHandlers(): void {
  if (typeof window === 'undefined') return;

  const globalScope = globalThis as GlobalWithRegistry;
  const registry = globalScope[REGISTRY_KEY];
  if (!registry) return;

  window.removeEventListener(
    'unhandledrejection',
    registry.onUnhandledRejection,
  );
  window.removeEventListener('error', registry.onError);

  delete globalScope[REGISTRY_KEY];
}

// Before Vite hot-swaps this module, tear down the live listeners so the fresh
// module instance re-registers exactly one of each — no accumulation.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unregisterGlobalErrorHandlers();
  });
}
